import React, { createContext, useState } from 'react';
import { ExternalProvider } from '../../../../lib';
import WithingOAuthModal from '../components/ExternalProvider/WithingOAuthModal';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store/store';
import { post } from '../../../../api/baseUrl';
import type { WithingsStartAuthResponse } from '../types/withings';

export const ExternalAuthContext = createContext<{
  open: boolean;
  isGettingAuthUrl: boolean;
  onOpen: () => void;
  onClose: () => void;
  startAuth: (provider: ExternalProvider) => void;
}>({
  open: false,
  isGettingAuthUrl: false,
  onOpen: () => {},
  onClose: () => {},
  startAuth: () => {},
});

export const useExternalAuth = () => {
  return React.useContext(ExternalAuthContext);
};

const ExternalAuthProvider = ({
  provider,
  children,
}: {
  provider: ExternalProvider;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isGettingAuthUrl, setIsGettingAuthUrl] = useState<boolean>(false);
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { patientProfile } = useSelector(
    (state: RootState) => state.patientProfile,
  );

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setIsGettingAuthUrl(false);
  };

  const startAuth = async (prov: ExternalProvider) => {
    if (prov !== ExternalProvider.WITHINGS) return;
    setIsGettingAuthUrl(true);
    try {
      const {
        data: { data },
      } = await post<WithingsStartAuthResponse>(
        'external-connections/withings/start',
        {
          tenancyId: selectedTenancy?.tenancy_id,
          access_token: patientProfile?.access_token,
        },
      );
      if (!data?.authorizationUrl) {
        return;
      }

      onOpen();
      window.location.href = data.authorizationUrl;
    } catch (error) {
      console.error(error);
    } finally {
      setIsGettingAuthUrl(false);
    }
  };

  return (
    <ExternalAuthContext.Provider
      value={{
        open,
        onOpen,
        onClose,
        startAuth,
        isGettingAuthUrl,
      }}
    >
      {children}
      {provider === ExternalProvider.WITHINGS && (
        <WithingOAuthModal provider={ExternalProvider.WITHINGS} />
      )}
    </ExternalAuthContext.Provider>
  );
};

export default ExternalAuthProvider;
